import { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, Loader2, Building2 } from "lucide-react";
import {
  SUB_LABEL, RENTAL_INCOME_SUBS, RENTAL_EXPENSE_SUBS, fmt,
} from "./constants";
import UndoToBusinessButton from "./UndoToBusinessButton";
import BiSelect from "@/components/BiSelect";
import BiDatePicker from "@/components/BiDatePicker";
import ConfirmDeleteDialog from "@/components/ConfirmDeleteDialog";
import { formatDateShort } from "@/shared/format";

/**
 * Rentals tab — property registry + income/expense entries per property.
 * Owned by <PersonalTaxPage /> which passes `properties`, `entries` filtered
 * to rental_income + rental_expense, and `onChange` to trigger a full refresh.
 */
export default function RentalsTab({ properties, entries, onChange }) {
  const [showAddProp, setShowAddProp] = useState(false);
  const [propForm, setPropForm] = useState({ address: "", ownership_pct: 100 });
  const [pendingDeleteProp, setPendingDeleteProp] = useState(null);
  const [showAddEntry, setShowAddEntry] = useState(null); // property_id | null
  const [entryForm, setEntryForm] = useState({
    kind: "income",
    sub_category: "rent_gross",
    amount: "",
    date: new Date().toISOString().slice(0, 10),
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  const saveProperty = async () => {
    if (!propForm.address.trim()) { toast.error("Address is required"); return; }
    setBusy(true);
    try {
      await api.post("/personal-tax/properties", {
        address: propForm.address.trim(),
        ownership_pct: Number(propForm.ownership_pct) || 100,
      });
      toast.success("Property added");
      setShowAddProp(false);
      setPropForm({ address: "", ownership_pct: 100 });
      await onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save property");
    } finally { setBusy(false); }
  };

  const removeProperty = (pid) => setPendingDeleteProp(pid);
  const confirmRemoveProperty = async () => {
    const pid = pendingDeleteProp;
    setPendingDeleteProp(null);
    if (!pid) return;
    try {
      await api.delete(`/personal-tax/properties/${pid}`);
      toast.success("Property removed");
      await onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    }
  };

  const saveEntry = async (pid) => {
    if (!Number(entryForm.amount)) { toast.error("Amount required"); return; }
    setBusy(true);
    try {
      await api.post("/personal-tax/entries", {
        type: entryForm.kind === "income" ? "rental_income" : "rental_expense",
        sub_category: entryForm.sub_category,
        amount: Number(entryForm.amount),
        date: entryForm.date,
        notes: entryForm.notes,
        property_id: pid,
        source: "manual",
      });
      toast.success("Entry saved");
      setShowAddEntry(null);
      setEntryForm({
        kind: "income", sub_category: "rent_gross", amount: "",
        date: new Date().toISOString().slice(0, 10), notes: "",
      });
      await onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  const removeEntry = async (eid) => {
    try {
      await api.delete(`/personal-tax/entries/${eid}`);
      await onChange();
    } catch (_err) {
      toast.error("Could not delete");
    }
  };

  // Precompute per-property income/expense/totals once per (properties, entries)
  // change — avoids the double `.filter()` + `.sort()` + `.reduce()` chain
  // running on every property row on every re-render (dropdowns, dialogs).
  const perProperty = useMemo(() => {
    const map = {};
    for (const p of properties) {
      const pIncome = entries.filter((e) => e.type === "rental_income" && e.property_id === p.id);
      const pExpense = entries.filter((e) => e.type === "rental_expense" && e.property_id === p.id);
      const gross = pIncome.reduce((s, e) => s + Number(e.amount || 0), 0);
      const exp = pExpense.reduce((s, e) => s + Number(e.amount || 0), 0);
      const rows = [...pIncome, ...pExpense].sort(
        (a, b) => (b.date || "").localeCompare(a.date || "")
      );
      map[p.id] = { gross, exp, rows };
    }
    return map;
  }, [properties, entries]);

  return (
    <div className="space-y-3" data-testid="rentals-tab">
      {properties.length === 0 && !showAddProp && (
        <div className="bi-surface p-4 text-center space-y-2">
          <Building2 className="w-6 h-6 text-white/40 mx-auto" />
          <div className="text-sm text-white/70">No rental properties on file.</div>
          <button
            type="button"
            data-testid="add-first-property"
            onClick={() => setShowAddProp(true)}
            className="h-10 px-4 rounded-lg bi-accent-btn text-xs font-heading font-bold uppercase tracking-wider inline-flex items-center gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" /> Add your first property
          </button>
        </div>
      )}

      {showAddProp && (
        <div className="bi-surface p-4 space-y-2" data-testid="add-property-form">
          <div className="font-heading font-bold text-sm">Add rental property</div>
          <input
            data-testid="prop-address"
            className="w-full bi-input"
            placeholder="12 Wattle St, Redfern NSW 2016"
            value={propForm.address}
            onChange={(e) => setPropForm({ ...propForm, address: e.target.value })}
          />
          <input
            data-testid="prop-ownership"
            className="w-full bi-input"
            type="number"
            min="0" max="100"
            placeholder="Ownership % (default 100)"
            value={propForm.ownership_pct}
            onChange={(e) => setPropForm({ ...propForm, ownership_pct: e.target.value })}
          />
          <div className="flex gap-2">
            <button
              data-testid="prop-save"
              onClick={saveProperty}
              disabled={busy}
              className="flex-1 h-11 rounded-xl bi-accent-btn text-xs font-heading font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
            </button>
            <button
              onClick={() => setShowAddProp(false)}
              className="h-11 px-4 rounded-xl bi-ghost-btn text-xs font-heading font-bold uppercase tracking-wider"
            >Cancel</button>
          </div>
        </div>
      )}

      {properties.map((p) => {
        const { gross, exp, rows } = perProperty[p.id] || { gross: 0, exp: 0, rows: [] };
        return (
          <div key={p.id} className="bi-surface p-4 space-y-2" data-testid={`property-${p.id}`}>
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="font-heading font-bold text-sm truncate">{p.address}</div>
                <div className="text-[10px] text-white/40">
                  Ownership {p.ownership_pct}%
                  {p.purchase_date && ` · Purchased ${p.purchase_date}`}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[9px] uppercase tracking-[0.2em] text-white/40 font-heading">Net</div>
                <div className="font-heading font-extrabold text-white">{fmt(gross - exp)}</div>
              </div>
              <button
                data-testid={`prop-delete-${p.id}`}
                onClick={() => removeProperty(p.id)}
                className="w-8 h-8 rounded-lg text-white/40 hover:text-red-400 hover:bg-white/5 transition inline-flex items-center justify-center"
                title="Delete property"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 text-[11px]">
              <div className="rounded-md bg-emerald-500/10 border border-emerald-500/20 p-2">
                <div className="text-emerald-400/80 uppercase tracking-wider text-[9px] font-heading">Gross</div>
                <div className="text-white font-bold">{fmt(gross)}</div>
              </div>
              <div className="rounded-md bg-red-500/10 border border-red-500/20 p-2">
                <div className="text-red-400/80 uppercase tracking-wider text-[9px] font-heading">Expenses</div>
                <div className="text-white font-bold">{fmt(exp)}</div>
              </div>
            </div>

            {rows.map((e) => (
              <div key={e.id} data-testid={`rent-entry-${e.id}`} className="flex items-center justify-between text-[11px] py-1 border-b border-white/5">
                <div className="min-w-0">
                  <div className={`truncate ${e.type === "rental_income" ? "text-emerald-300" : "text-white/90"}`}>
                    {SUB_LABEL[e.sub_category] || e.sub_category || (e.type === "rental_income" ? "Income" : "Expense")}
                  </div>
                  <div className="text-white/40 text-[10px]">{formatDateShort(e.date)}{e.notes ? ` · ${e.notes}` : ""}</div>
                </div>
                <div className="tabular-nums font-bold text-white">
                  {e.type === "rental_income" ? "+" : "-"}{fmt(e.amount)}
                </div>
                <UndoToBusinessButton entry={e} onDone={onChange} testIdSuffix={`rent-${e.id}`} />
                <button
                  data-testid={`entry-delete-${e.id}`}
                  onClick={() => removeEntry(e.id)}
                  className="ml-2 text-white/30 hover:text-red-400"
                  title="Delete"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}

            {showAddEntry === p.id ? (
              <div className="bi-surface p-3 space-y-2 mt-2" data-testid={`add-entry-form-${p.id}`}>
                <div className="grid grid-cols-2 gap-2">
                  <BiSelect
                    data-testid={`entry-kind-${p.id}`}
                    className="bi-input h-10"
                    value={entryForm.kind}
                    onChange={(e) => {
                      const kind = e.target.value;
                      setEntryForm({
                        ...entryForm, kind,
                        sub_category: kind === "income" ? "rent_gross" : "agent_fees",
                      });
                    }}
                  >
                    <option value="income">Income</option>
                    <option value="expense">Expense</option>
                  </BiSelect>
                  <BiSelect
                    data-testid={`entry-sub-${p.id}`}
                    className="bi-input h-10"
                    value={entryForm.sub_category}
                    onChange={(e) => setEntryForm({ ...entryForm, sub_category: e.target.value })}
                  >
                    {(entryForm.kind === "income" ? RENTAL_INCOME_SUBS : RENTAL_EXPENSE_SUBS).map((s) => (
                      <option key={s} value={s}>{SUB_LABEL[s]}</option>
                    ))}
                  </BiSelect>
                </div>
                <input
                  data-testid={`entry-amount-${p.id}`}
                  className="w-full bi-input"
                  type="number"
                  step="0.01"
                  placeholder="Amount (AUD)"
                  value={entryForm.amount}
                  onChange={(e) => setEntryForm({ ...entryForm, amount: e.target.value })}
                />
                <BiDatePicker
                  data-testid={`entry-date-${p.id}`}
                  className="w-full bi-input"
                  value={entryForm.date}
                  onChange={(e) => setEntryForm({ ...entryForm, date: e.target.value })}
                />
                <input
                  data-testid={`entry-notes-${p.id}`}
                  className="w-full bi-input"
                  placeholder="Notes (optional)"
                  value={entryForm.notes}
                  onChange={(e) => setEntryForm({ ...entryForm, notes: e.target.value })}
                />
                <div className="flex gap-2">
                  <button
                    data-testid={`entry-save-${p.id}`}
                    onClick={() => saveEntry(p.id)}
                    disabled={busy}
                    className="flex-1 h-10 rounded-lg bi-accent-btn text-xs font-heading font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
                  </button>
                  <button
                    onClick={() => setShowAddEntry(null)}
                    className="h-10 px-4 rounded-lg bi-ghost-btn text-xs font-heading font-bold uppercase tracking-wider"
                  >Cancel</button>
                </div>
              </div>
            ) : (
              <button
                data-testid={`add-entry-${p.id}`}
                onClick={() => setShowAddEntry(p.id)}
                className="w-full h-9 rounded-lg text-[11px] text-white/60 hover:text-white/90 border border-dashed border-white/10 hover:border-white/30 inline-flex items-center justify-center gap-1.5"
              >
                <Plus className="w-3.5 h-3.5" /> Add income or expense
              </button>
            )}
          </div>
        );
      })}

      {properties.length > 0 && !showAddProp && (
        <button
          data-testid="add-property"
          onClick={() => setShowAddProp(true)}
          className="w-full h-11 rounded-xl bi-ghost-btn text-xs font-heading font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2"
        >
          <Plus className="w-3.5 h-3.5" /> Add another property
        </button>
      )}

      <ConfirmDeleteDialog
        open={!!pendingDeleteProp}
        title="Delete this property?"
        description="Entries logged against this property must be removed first. This can't be undone."
        onConfirm={confirmRemoveProperty}
        onCancel={() => setPendingDeleteProp(null)}
        testId="rental-property-delete"
      />
    </div>
  );
}
